// Global runtime guard: fixes unreadable text (white-on-white / dark-on-dark).
const DARK = "#18181b";
const LIGHT = "#fafafa";
const DARK_RGB = [24, 24, 27, 1];
const LIGHT_RGB = [250, 250, 250, 1];
const fixed = new Map();

const parse = (c) => {
  const m = c && c.match(/[\d.]+/g);
  if (!m || m.length < 3) return null;
  return [+m[0], +m[1], +m[2], m.length > 3 ? +m[3] : 1];
};
const blend = (top, bot) => {
  const a = top[3];
  return [0, 1, 2].map((i) => top[i] * a + bot[i] * (1 - a)).concat(1);
};
const lumi = (c) => {
  const f = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(c[0]) + 0.7152 * f(c[1]) + 0.0722 * f(c[2]);
};
const ratio = (a, b) => { const x = lumi(a), y = lumi(b); return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05); };

const gradientAvg = (img) => {
  if (img.includes("url(")) return null;
  const stops = (img.match(/rgba?\([^)]+\)/g) || []).map(parse).filter(Boolean);
  if (!stops.length) return null;
  return [0, 1, 2, 3].map((i) => stops.reduce((a, c) => a + c[i], 0) / stops.length);
};

const WHITE = [255, 255, 255, 1];
let memo = new Map();
let mediaRects = [];

// Composited opaque background color behind a node, or null when unknown (image).
const bgOf = (node) => {
  if (!node || node.nodeType !== 1) return WHITE;
  if (memo.has(node)) return memo.get(node);
  const s = getComputedStyle(node);
  const own = [];
  if (s.backgroundImage && s.backgroundImage !== "none") {
    const g = gradientAvg(s.backgroundImage);
    if (!g) { memo.set(node, null); return null; }
    own.push(g);
  }
  const b = parse(s.backgroundColor);
  if (b && b[3] > 0) own.push(b);
  const opaqueIdx = own.findIndex((l) => l[3] >= 0.95);
  let base = opaqueIdx >= 0 ? own[opaqueIdx] : bgOf(node.parentElement);
  if (base) {
    const top = opaqueIdx >= 0 ? own.slice(0, opaqueIdx) : own;
    for (let i = top.length - 1; i >= 0; i--) base = blend(top[i], base);
  }
  memo.set(node, base);
  return base;
};

const overMedia = (el) => {
  if (!mediaRects.length) return false;
  const r = el.getBoundingClientRect();
  const cx = r.left + r.width / 2, cy = r.top + r.height / 2;
  return mediaRects.some((b) => cx >= b.left && cx <= b.right && cy >= b.top && cy <= b.bottom);
};

const check = (el) => {
  if (el.closest("[data-no-contrast-guard]")) return;
  const s = getComputedStyle(el);
  if (s.visibility === "hidden" || s.display === "none") return;
  const c = parse(s.color);
  if (!c || c[3] === 0) return;
  const bg = bgOf(el);
  if (!bg || overMedia(el)) return;
  const fg = c[3] < 1 ? blend(c, bg) : c;
  if (ratio(fg, bg) >= 3) return;
  fixed.set(el, [el.style.getPropertyValue("color"), el.style.getPropertyPriority("color")]);
  el.style.setProperty("color", ratio(DARK_RGB, bg) >= ratio(LIGHT_RGB, bg) ? DARK : LIGHT, "important");
  el.setAttribute("data-cg", "1");
};

const scan = () => {
  fixed.forEach(([val, prio], el) => {
    if (val) el.style.setProperty("color", val, prio); else el.style.removeProperty("color");
    el.removeAttribute("data-cg");
  });
  fixed.clear();
  memo = new Map();
  mediaRects = [...document.querySelectorAll("img, video, canvas")]
    .map((m) => m.getBoundingClientRect())
    .filter((r) => r.width * r.height > 40000);
  const targets = new Set();
  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (n.textContent.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT),
  });
  while (walker.nextNode()) {
    const p = walker.currentNode.parentElement;
    if (p && !["SCRIPT", "STYLE", "NOSCRIPT", "OPTION"].includes(p.tagName)) targets.add(p);
  }
  document.querySelectorAll("input, textarea, select, svg.lucide").forEach((e) => targets.add(e));
  targets.forEach((el) => { try { check(el); } catch (e) { /* ignore */ } });
};

export const startContrastGuard = () => {
  let timer = null;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(scan, 400); };
  const obs = new MutationObserver(schedule);
  const start = () => {
    obs.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["class"] });
    schedule();
  };
  if (document.body) start(); else window.addEventListener("DOMContentLoaded", start);
  window.__contrastScan = scan;
};
